import { useMemo, useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Logo, NafolaMark } from '../components/Logo';
import { useScrollReveal, useStickyProgress } from '../hooks/useScrollReveal';

export type Persona = 'sender' | 'deliverer' | 'tracking';

const VEHICLE_TILES = [
  { id: 'bike', label: 'Vélo', hint: 'Le quartier' },
  { id: 'motorbike', label: 'Moto', hint: 'Plus vite' },
  { id: 'car', label: 'Voiture', hint: 'Un plus gros colis' },
  { id: 'van', label: 'Camionnette', hint: 'Plusieurs cartons' },
  { id: 'small_truck', label: 'Petit camion', hint: 'Un vrai volume' },
  { id: 'refrigerated', label: 'Réfrigéré', hint: 'À garder au frais' }
];

const STEPS = [
  { n: '1', title: 'Les deux adresses', text: 'Vous dites où prendre le colis et où le déposer.' },
  { n: '2', title: 'Un livreur proche', text: 'Quelqu’un de disponible dans la zone prend la course.' },
  { n: '3', title: 'Le colis part', text: 'Le livreur le récupère, prend une photo, et se met en route.' },
  { n: '4', title: 'Remis à la porte', text: 'La personne donne 4 chiffres. Le livreur confirme la remise.' }
];

const QUESTIONS = [
  {
    q: 'Faut-il déjà être inscrit ailleurs ?',
    a: 'Non. N’importe qui peut créer un compte, envoyer un colis ou devenir livreur.'
  },
  {
    q: 'Qui reçoit le prix de la course ?',
    a: 'Le livreur, en entier. Le prix des marchandises est seulement une indication : ce n’est pas son revenu.'
  },
  {
    q: 'Comment sait-on que le bon colis a été remis ?',
    a: 'Une photo au départ, puis les 4 chiffres que la personne donne à l’arrivée, avec une signature.'
  },
  {
    q: 'Je n’ai qu’un téléphone. Est-ce suffisant ?',
    a: 'Oui. L’application montre les courses en cours, l’historique, le profil et, pour un livreur, s’il est disponible.'
  },
  {
    q: 'Dans quelles villes Nafola fonctionne-t-il ?',
    a: 'Pour l’instant nous couvrons surtout Libreville et les communes proches (Glass, Owendo, Akanda…). Chaque livreur définit sa propre zone.'
  },
  {
    q: 'Comment est calculé le prix d’une course ?',
    a: 'Selon la distance et le tarif du livreur (prix au kilomètre et minimum). Vous voyez le montant avant de confirmer.'
  },
  {
    q: 'Quels moyens de paiement sont acceptés ?',
    a: 'Espèces à la remise, mobile money, carte ou virement selon ce que le livreur accepte. Le choix se fait à la création de la course.'
  },
  {
    q: 'Comment suivre mon colis ?',
    a: 'Avec le code qui commence par NF- (sur le site, sans compte) ou depuis « Mes courses » une fois connecté. Le statut change en direct : acceptée, en route, livrée.'
  },
  {
    q: 'Je suis commerçant. Puis-je envoyer plusieurs colis ?',
    a: 'Oui. Créez un compte entreprise : vous lancez des courses pour vos clients, avec photo de profil et historique dédié.'
  },
  {
    q: 'Que se passe-t-il si personne n’est là à l’arrivée ?',
    a: 'Le livreur contacte le destinataire. Sans remise possible, la course peut être marquée en échec ; vous pouvez en recréer une autre vers une nouvelle adresse.'
  }
];

const PERSONA_BUTTONS: Array<{ key: Persona; title: string; subtitle: string; icon: ReactNode }> = [
  {
    key: 'sender',
    title: 'Je cherche un livreur',
    subtitle: 'Vélo, moto ou voiture, près de chez moi',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
      </svg>
    )
  },
  {
    key: 'deliverer',
    title: 'Je veux livrer',
    subtitle: 'Je m’inscris et je prends des courses sur mon téléphone',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 17a2 2 0 11-4 0 2 2 0 014 0zm10 0a2 2 0 11-4 0 2 2 0 014 0zm-7 0H7m12 0h-2m-2-9h3l3 5v4h-2M3 5h11v12H3V5z"
        />
      </svg>
    )
  },
  {
    key: 'tracking',
    title: 'Je suis une course',
    subtitle: 'J’ai un code qui commence par NF-',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12l2 2 4-4M12 2l9 4v6c0 5.55-3.84 10.74-9 12-5.16-1.26-9-6.45-9-12V6l9-4z"
        />
      </svg>
    )
  }
];

interface Props {
  firstName?: string;
  role?: string;
  persona: Persona;
  onOpen: (key: Persona) => void;
  onVehicle: (id: string) => void;
  panel: ReactNode;
}

function Reveal({
  children,
  className = '',
  delay
}: {
  children: ReactNode;
  className?: string;
  delay?: 1 | 2;
}) {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={`reveal ${visible ? 'is-visible' : ''} ${delay === 1 ? 'reveal-delay-1' : ''} ${
        delay === 2 ? 'reveal-delay-2' : ''
      } ${className}`}
    >
      {children}
    </div>
  );
}

export const HomeLanding = ({ firstName, role, persona, onOpen, onVehicle, panel }: Props) => {
  const loggedIn = Boolean(firstName);
  const canSend = !role || role === 'client' || role === 'business' || role === 'admin';
  const sendTo = loggedIn && canSend ? '/orders/new' : loggedIn ? '/orders' : '/register?role=client';
  const deliverTo = role === 'deliverer' || role === 'admin' ? '/deliverer' : '/register?role=deliverer';

  const stickyRef = useRef<HTMLElement | null>(null);
  const progress = useStickyProgress(stickyRef);
  const activeStep = Math.min(STEPS.length - 1, Math.floor(progress * STEPS.length));

  const scrollHint = useMemo(
    () => (
      <button
        type="button"
        className="group mt-14 inline-flex flex-col items-center gap-2 text-slate-500 transition hover:text-brand-600"
        onClick={() => document.getElementById('story')?.scrollIntoView({ behavior: 'smooth' })}
      >
        <span className="text-[11px] font-semibold uppercase tracking-[0.22em]">Défiler</span>
        <span className="flex h-10 w-6 items-start justify-center rounded-full border border-current/30 p-1">
          <span className="mt-1 h-2 w-1 rounded-full bg-current opacity-70 transition group-hover:translate-y-1" />
        </span>
      </button>
    ),
    []
  );

  return (
    <div className="landing-snap">
      {/* —— HERO plein écran —— */}
      <section className="landing-panel relative flex min-h-[100svh] flex-col overflow-hidden">
        <div
          className="absolute inset-0 bg-[radial-gradient(ellipse_90%_70%_at_50%_-10%,rgba(24,112,240,0.28),transparent_55%),linear-gradient(165deg,#0b1220_0%,#12233f_42%,#1870F0_120%)]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)',
            backgroundSize: '72px 72px'
          }}
          aria-hidden
        />
        <div className="pointer-events-none absolute -right-24 top-1/4 h-80 w-80 rounded-full bg-brand-500/30 blur-3xl animate-soft-pulse" aria-hidden />
        <div className="pointer-events-none absolute -left-16 bottom-0 h-72 w-72 rounded-full bg-amber-400/20 blur-3xl" aria-hidden />

        <div className="relative z-10 mx-auto flex w-full max-w-6xl flex-1 flex-col justify-center px-4 pb-16 pt-10 sm:px-6 lg:pt-6">
          {loggedIn && (
            <p className="hero-rise mb-6 text-sm font-medium text-brand-100">
              Bonjour {firstName}
              {role ? ` · ${role}` : ''}
            </p>
          )}

          <div className="hero-rise">
            <Logo tone="white" className="scale-[1.35] origin-left sm:scale-[1.55]" />
          </div>

          <h1 className="hero-rise-delay mt-8 max-w-4xl text-4xl font-bold tracking-tight text-white sm:text-6xl sm:leading-[1.05] lg:text-7xl">
            Un colis, d’une porte à l’autre.
          </h1>
          <p className="hero-rise-delay mt-5 max-w-xl text-lg leading-relaxed text-slate-200 sm:text-xl">
            Indiquez le départ et l’arrivée. Un livreur proche s’en charge. Vous suivez jusqu’à la porte.
          </p>

          <div className="hero-rise-delay mt-10 flex flex-wrap items-center gap-3">
            <Link
              to={sendTo}
              className="inline-flex items-center justify-center rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-slate-900 shadow-lg transition hover:bg-slate-100"
            >
              {loggedIn && canSend ? 'Envoyer un colis' : loggedIn ? 'Voir mes courses' : 'Commencer'}
            </Link>
            <button
              type="button"
              onClick={() => onOpen('tracking')}
              className="inline-flex items-center justify-center rounded-full border border-white/35 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/12"
            >
              J’ai un code de suivi
            </button>
          </div>

          <div className="hero-rise-delay mt-4">{scrollHint}</div>
        </div>

        <div className="pointer-events-none absolute bottom-[-8%] right-[-6%] hidden w-[42%] max-w-xl opacity-90 lg:block" aria-hidden>
          <div className="hero-float">
            <HeroVisual />
          </div>
        </div>
      </section>

      {/* —— STORY sticky (défilement Apple) —— */}
      <section id="story" ref={stickyRef} className="relative h-[320vh] bg-[#f5f7fb]">
        <div className="sticky top-0 flex min-h-[100svh] items-center overflow-hidden">
          <div className="mx-auto grid w-full max-w-6xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-600">Le parcours</p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl">
                Comment ça se passe
              </h2>
              <p className="mt-4 max-w-md text-base leading-relaxed text-slate-600 sm:text-lg">
                Faites défiler : chaque étape s’illumine, comme sur une page produit.
              </p>
              <ol className="mt-10 space-y-3">
                {STEPS.map((step, i) => {
                  const on = i === activeStep;
                  const done = i < activeStep;
                  return (
                    <li
                      key={step.n}
                      className={`rounded-2xl border px-4 py-3 transition-all duration-500 ${
                        on
                          ? 'border-brand-500 bg-white shadow-md ring-2 ring-brand-500/15'
                          : done
                            ? 'border-emerald-200 bg-emerald-50/60'
                            : 'border-transparent bg-transparent opacity-45'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                            on
                              ? 'bg-brand-500 text-white'
                              : done
                                ? 'bg-emerald-500 text-white'
                                : 'bg-slate-200 text-slate-500'
                          }`}
                        >
                          {done ? '✓' : step.n}
                        </span>
                        <div>
                          <p className="font-semibold text-slate-900">{step.title}</p>
                          <p
                            className={`text-sm leading-relaxed text-slate-600 transition-opacity duration-500 ${
                              on ? 'opacity-100' : 'opacity-70'
                            }`}
                          >
                            {step.text}
                          </p>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>

            <div className="relative mx-auto hidden h-[420px] w-full max-w-md lg:block" aria-hidden>
              <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-br from-brand-500/20 via-white to-amber-100/80 shadow-soft" />
              <div
                className="absolute inset-8 rounded-[2rem] border border-white/80 bg-white/70 p-8 backdrop-blur transition-all duration-700"
                style={{ transform: `translateY(${(1 - progress) * 18}px)`, opacity: 0.85 + progress * 0.15 }}
              >
                <NafolaMark className="h-10 w-14 text-brand-500" />
                <p className="mt-8 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Étape</p>
                <p className="mt-2 text-3xl font-bold tracking-tight text-slate-900">{STEPS[activeStep].title}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{STEPS[activeStep].text}</p>
                <div className="mt-8 h-1.5 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-brand-500 transition-[width] duration-300 ease-out"
                    style={{ width: `${Math.max(8, progress * 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* —— Véhicules —— */}
      <section className="landing-panel border-y border-slate-200/80 bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-600">Selon le colis</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Le bon véhicule</h2>
            <p className="mt-3 max-w-xl text-slate-600">Choisissez, puis on cherche un livreur disponible.</p>
          </Reveal>
          <Reveal delay={1} className="mt-10">
            <div className="flex gap-3 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {VEHICLE_TILES.map((tile) => (
                <button
                  key={tile.id}
                  type="button"
                  onClick={() => onVehicle(tile.id)}
                  className="min-w-[10rem] shrink-0 rounded-2xl border border-slate-200 bg-slate-50/80 px-5 py-4 text-left transition hover:border-brand-500 hover:bg-brand-50"
                >
                  <p className="font-semibold text-slate-900">{tile.label}</p>
                  <p className="mt-1 text-xs text-slate-500">{tile.hint}</p>
                </button>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* —— Deux profils —— */}
      <section className="landing-panel bg-[#f5f7fb] py-20 sm:py-28">
        <div className="mx-auto grid max-w-6xl gap-5 px-4 sm:px-6 lg:grid-cols-2">
          <Reveal>
            <article className="flex h-full flex-col justify-between overflow-hidden rounded-[2rem] bg-brand-600 p-8 text-white sm:p-10">
              <div>
                <p className="text-sm font-semibold text-brand-100">Vous envoyez</p>
                <h2 className="mt-3 text-3xl font-bold tracking-tight">La course se suit toute seule</h2>
                <p className="mt-4 max-w-md text-base leading-relaxed text-brand-50">
                  Commande, acceptée, collectée, en route, livrée. Les adresses et le destinataire restent visibles.
                </p>
              </div>
              <Link to={loggedIn ? '/orders' : '/register?role=client'} className="btn-ghost mt-10 w-fit bg-white">
                {loggedIn ? 'Voir mes courses' : 'Créer un compte'}
              </Link>
            </article>
          </Reveal>
          <Reveal delay={1}>
            <article className="flex h-full flex-col justify-between overflow-hidden rounded-[2rem] bg-amber-500 p-8 text-amber-950 sm:p-10">
              <div>
                <p className="text-sm font-semibold text-amber-950/75">Vous livrez</p>
                <h2 className="mt-3 text-3xl font-bold tracking-tight">Tout tient dans le téléphone</h2>
                <p className="mt-4 max-w-md text-base leading-relaxed text-amber-950/90">
                  Disponibilité, prise de course, avance jusqu’à la porte. Le prix de la course est pour vous.
                </p>
              </div>
              <Link to={deliverTo} className="btn-primary mt-10 w-fit bg-slate-900 hover:bg-slate-800">
                {role === 'deliverer' ? 'Ouvrir ma tournée' : 'Devenir livreur'}
              </Link>
            </article>
          </Reveal>
        </div>
      </section>

      {/* —— App mobile —— */}
      <section className="landing-panel relative overflow-hidden bg-slate-950 text-white">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_80%_40%,rgba(24,112,240,0.35),transparent)]" aria-hidden />
        <div className="relative mx-auto grid min-h-[100svh] max-w-6xl items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-2">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-300">Application</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">Pour la journée du livreur</h2>
            <ul className="mt-8 space-y-5 text-base leading-relaxed text-slate-300">
              <li>
                <strong className="text-white">En cours.</strong> Les courses à prendre ou déjà commencées.
              </li>
              <li>
                <strong className="text-white">Historique.</strong> Ce qui est livré, annulé, et les montants.
              </li>
              <li>
                <strong className="text-white">Profil.</strong> Identité, véhicule, et le bouton pour faire une pause.
              </li>
            </ul>
          </Reveal>
          <Reveal delay={1} className="flex justify-center lg:justify-end">
            <PhonePreview />
          </Reveal>
        </div>
      </section>

      {/* —— FAQ + actions —— */}
      <section className="landing-panel bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <Reveal>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Questions fréquentes</h2>
          </Reveal>
          <Reveal delay={1} className="mt-6">
            <div className="divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
              {QUESTIONS.map((item) => (
                <details key={item.q} className="group px-5 py-4">
                  <summary className="cursor-pointer list-none font-medium text-slate-900">
                    <span className="flex items-center justify-between gap-4">
                      {item.q}
                      <span className="text-xl leading-none text-slate-400 transition group-open:rotate-45" aria-hidden>
                        +
                      </span>
                    </span>
                  </summary>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.a}</p>
                </details>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="mx-auto mt-20 max-w-6xl px-4 sm:px-6">
          <Reveal>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900">À vous de jouer</h2>
          </Reveal>
          <Reveal delay={1} className="mt-8">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {PERSONA_BUTTONS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => onOpen(item.key)}
                  className={`group flex flex-col items-start gap-3 rounded-2xl border p-5 text-left transition-all ${
                    persona === item.key
                      ? 'border-brand-500 bg-white shadow-md ring-2 ring-brand-500/15'
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  }`}
                >
                  <span
                    className={`flex h-11 w-11 items-center justify-center rounded-xl ${
                      persona === item.key ? 'bg-brand-500 text-white' : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {item.icon}
                  </span>
                  <div>
                    <p className="font-semibold text-slate-900">{item.title}</p>
                    <p className="mt-1 text-sm text-slate-600">{item.subtitle}</p>
                  </div>
                </button>
              ))}
            </div>
          </Reveal>

          <section
            id="persona-panel"
            className="mt-6 scroll-mt-24 rounded-2xl border border-slate-200 bg-white p-6 shadow-soft sm:p-8"
          >
            {panel}
          </section>
        </div>
      </section>

      <footer className="border-t border-slate-200/80 bg-slate-50 py-8 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} nafola
      </footer>
    </div>
  );
};

const HeroVisual = () => (
  <div className="relative aspect-square w-full">
    <div className="absolute inset-[12%] rounded-full border border-white/15" />
    <div className="absolute inset-[22%] rounded-full border border-white/10" />
    <div className="absolute left-1/2 top-1/2 flex h-40 w-40 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[2rem] bg-white/10 shadow-2xl ring-1 ring-white/25 backdrop-blur-md">
      <NafolaMark className="h-20 w-[6.5rem] text-white" />
    </div>
    <div className="absolute left-[8%] top-[22%] rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-white backdrop-blur">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-white/70">En route</p>
      <p className="mt-1 text-sm font-semibold">18,90 EUR</p>
    </div>
    <div className="absolute bottom-[18%] right-[6%] rounded-2xl border border-white/20 bg-slate-950/50 px-4 py-3 text-white backdrop-blur">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-white/70">Remise</p>
      <p className="mt-1 text-sm font-semibold">4 chiffres à la porte</p>
    </div>
  </div>
);

const PhonePreview = () => (
  <div
    className="w-[260px] rounded-[2.2rem] border-[8px] border-slate-700 bg-slate-50 p-4 text-slate-900 shadow-2xl"
    aria-hidden
  >
    <p className="text-[10px] font-bold uppercase tracking-wide text-brand-600">En cours</p>
    <p className="mt-1 text-lg font-bold">Bonjour</p>
    <div className="mt-3 rounded-xl bg-white p-3 shadow-sm">
      <p className="text-[10px] text-slate-400">Collecte</p>
      <p className="text-xs font-medium">22 Avenue Fictive</p>
      <p className="mt-2 text-[10px] text-slate-400">Livraison</p>
      <p className="text-xs font-medium">10 Rue de la Démo</p>
    </div>
    <div className="mt-5 flex justify-between text-[10px] font-semibold text-slate-400">
      <span className="text-brand-600">En cours</span>
      <span>Historique</span>
      <span>Profil</span>
    </div>
  </div>
);
